import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellDoubleClickedEvent, type IRowNode } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import TempWorkPopup from "@user/views/inoutbound/popup_temp_work";

interface State {
  gridApi: GridApi | null,
  workReqEtcNo: string,
  wh: string,
  pn: string,
  isShowTempWork: boolean
}

const defState: State = {
  gridApi: null,
  workReqEtcNo: "",
  wh: "",
  pn: "",
  isShowTempWork: false
};

interface SearchParams {
  inoutType: string,
  erpIfCompleteStatus: string,
  wh: string,
  pnLike: string,
  trNoLike: string,
  snLike: string,
  woLike: string,
  visitNoLike: string,
  requesterLike: string,
  workOrdererNameLike: string,
  workCompleteDateFrom: string,
  workCompleteDateTo: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  inoutType: "",
  erpIfCompleteStatus: "",
  wh: "",
  pnLike: "",
  trNoLike: "",
  snLike: "",
  woLike: "",
  visitNoLike: "",
  requesterLike: "",
  workOrdererNameLike: "",
  workCompleteDateFrom: dayjs().subtract(7, "day").format("YYYYMMDD") + "0000",
  workCompleteDateTo: dayjs().format("YYYYMMDD") + "2359",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "workReqErpNo", type: "InvalidStatus", msg: "이미 완료된 작업입니다."},
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`}
];

function TempWorkList(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    {
      headerName: "ERP 매칭", field: "erpMatchStatus", width: 120, cellStyle: { textAlign: "center", cursor: "pointer" }, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        return (commonCode.getCodeValue("erpMatchStatus", p.data.erpMatchStatus) + (p.data.erpMatchStatus == "C" ? `(${p.data.erpMatchWorkerName})` : ""));
      }
    },
    { headerName: "ERP I/F", field: "erpIfCompleteStatus", width: 80, cellStyle: { textAlign: "center", cursor: "pointer" }, valueFormatter: valueFormatter.commonCode("erpIfCompleteStatus") },
    { headerName: "Work Date", field: "workCompleteDate", width: 180, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "작업구분", field: "inoutType", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("inoutType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 180, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "W/O", headerClass: "hd-ta-left", field: "wo", width: 150, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Shop", headerClass: "hd-ta-left", field: "workShop", width: 120, cellStyle: { textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Work Qty", headerClass: "hd-ta-right", field: "completeQty", width: 110, cellStyle: {textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "요청자", field: "requesterName", width: 100, cellStyle: { textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업자", field: "workOrdererName", width: 100, cellStyle: { textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        return (
          (n.data.erpMatchStatus === "C" && n.data.erpIfCompleteStatus !== "C")
        );
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellDoubleClicked: (e: CellDoubleClickedEvent) => {
      if ((e.colDef.cellStyle as any)?.cursor != "pointer") {
        return;
      }
      openPopupTempWork(e.data);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/inoutbound/temp_work_list.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/inoutbound/temp_work_list", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/inoutbound/temp_work_list", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/inoutbound/temp_work_list", {});
  }

  async function execIf() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("작업을 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("실행하시겠습니까?") == false) {
      return false;
    }

    let success = 0; const fails: string[] = [];
    for (const info of selectedInfos) {
      try {
        const data = await kutil.sendPost("/api/inoutbound/temp_work_list.proc?mode=execIf", [info], {
          validationMsg: validationMsg
        });
        success++;
      } catch (e) {
        fails.push(info.workReqErpNo);
      }
    }

    await kutil.alert(`실행되었습니다.\r\n성공 ${success}건${fails.length ? `, 실패 ${fails.length}건` : ""}`);
    router.reload();
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/inoutbound/temp_work_list.download?mode=search", searchParams, {
        "fileName": "임시작업관리_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupTempWork(info: any) {
    setState(prev => ({ ...prev, isShowTempWork: true, workReqEtcNo: info.workReqEtcNo, wh: info.wh, pn: info.pn }));
  }

  async function closePopupTempWork(data: any) {
    setState(prev => ({ ...prev, isShowTempWork: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>임시작업 관리</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={execIf}>ERP I/F</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>작업구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.inoutType}
                onChange={(e) => {doSearch(e, {inoutType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.inoutType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>ERP I/F</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.erpIfCompleteStatus}
                onChange={(e) => {doSearch(e, {erpIfCompleteStatus: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.erpIfCompleteStatus as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>창고</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.wh}
                onChange={(e) => {doSearch(e, {wh: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.wh as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>P/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.pnLike}
              onChange={(e) => {updateSearchParams({ pnLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="P/N"
            />
          </div>
          <div>
            <p>TR No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.trNoLike}
              onChange={(e) => {updateSearchParams({ trNoLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="TR No"
            />
          </div>
          <div>
            <p>S/N</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.snLike}
              onChange={(e) => {updateSearchParams({ snLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="S/N"
            />
          </div>
          <div>
            <p>W/O</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.woLike}
              onChange={(e) => {updateSearchParams({ woLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="W/O"
            />
          </div>
          <div>
            <p>Visit No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.visitNoLike}
              onChange={(e) => {updateSearchParams({ visitNoLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Visit No"
            />
          </div>
          <div>
            <p>요청자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.requesterLike}
              onChange={(e) => {updateSearchParams({ requesterLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="요청자"
            />
          </div>
          <div>
            <p>작업자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.workOrdererNameLike}
              onChange={(e) => {updateSearchParams({ workOrdererNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="작업자"
            />
          </div>
          <div className="date-time-range">
            <p>조회일시</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.workCompleteDateFrom)}
                  onChange={(e) => doSearch(e, { workCompleteDateFrom: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  maxDateTime={dayjs(searchParams.workCompleteDateTo)}
                />
                <span>-</span>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.workCompleteDateTo)}
                  onChange={(e) => doSearch(e, { workCompleteDateTo: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  minDateTime={dayjs(searchParams.workCompleteDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellDoubleClicked={gridCallbacks.onCellDoubleClicked}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
        {state.isShowTempWork && <TempWorkPopup isShow={state.isShowTempWork} onClose={closePopupTempWork} id={props.id} data={{workReqEtcNo: state.workReqEtcNo, wh: state.wh, pn: state.pn}} />}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(TempWorkList);
